if (!customElements.get('collection-list')) {
  class CollectionList extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        sliderWrapper: '.collection-list__items',
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      };

      this.classes = {
        grid: 'f-grid',
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);
      this.cardStyle = this.dataset.cardStyle;
      this.itemSize = this.dataset.itemSize;
      this.columnsDesktop = this.dataset.columnsDesktop;

      this.sliderInstance = false;

      const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
      this.isProductTypeFilterList = Boolean(this.closest('product-type-filter-bar'));
      mql.onchange = this.init.bind(this);
      if (this.isProductTypeFilterList) {
        this.onFilterLayoutChange = this.onFilterLayoutChange.bind(this);
        window.addEventListener('resize', this.onFilterLayoutChange);
      }
      this.init();
    }

    onFilterLayoutChange() {
      if (!this.isProductTypeFilterList) return;
      this.init();
    }

    init() {
      if (FoxTheme.config.mqlMobile) {
        this.clearCenteredFilterLayout();
        this.destroySlider();
        return;
      }

      if (this.isProductTypeFilterList) {
        this.clearCenteredFilterLayout();
        this.initSlider();
        requestAnimationFrame(() => this.updateProductTypeFilterLayout());
        return;
      }

      this.initSlider();
    }

    clearCenteredFilterLayout() {
      this.classList.remove('is-centered');
      this.closest('product-type-filter-bar')?.classList.remove('is-centered');
    }

    updateProductTypeFilterLayout() {
      if (!this.isProductTypeFilterList || FoxTheme.config.mqlMobile) return;

      const slides = this.sliderWrapper?.querySelectorAll('.swiper-slide');
      if (!slides?.length) return;

      const gap = this.cardStyle == 'card' ? 8 : 20;
      let totalWidth = 0;

      slides.forEach((slide, index) => {
        totalWidth += slide.offsetWidth;
        if (index > 0) totalWidth += gap;
      });

      const fits = totalWidth <= this.clientWidth;
      const filterBar = this.closest('product-type-filter-bar');

      if (fits) {
        this.destroySlider();
        this.classList.add('is-centered');
        filterBar?.classList.add('is-centered');
      } else {
        this.classList.remove('is-centered');
        filterBar?.classList.remove('is-centered');
        if (typeof this.sliderInstance !== 'object') {
          this.initSlider();
        }
      }
    }

    initSlider() {
      const sliderOptions = {
        modules: [FoxTheme.Swiper.Navigation, FoxTheme.Swiper.Pagination, FoxTheme.Swiper.Mousewheel],
        slidesPerView: 'auto',
        spaceBetween: 8,
        navigation: {
          nextEl: this.section.querySelector(this.selectors.nextEl),
          prevEl: this.section.querySelector(this.selectors.prevEl),
        },
        pagination: false,
        loop: false,
        threshold: 2,
        mousewheel: {
          enabled: true,
          forceToAxis: true,
        },
        breakpoints: {
          768: {
            spaceBetween: this.cardStyle == 'card' ? 8 : 20,
            slidesPerView: this.itemSize == 'fixed' ? (this.columnsDesktop > 4 ? 4 : this.columnsDesktop) : 'auto',
          },
          1024: {
            spaceBetween: this.cardStyle == 'card' ? 8 : 20,
            slidesPerView:
              this.itemSize == 'fixed'
                ? this.columnsDesktop > 6
                  ? this.columnsDesktop - 1
                  : this.columnsDesktop
                : 'auto',
          },
          1280: {
            spaceBetween: this.cardStyle == 'card' ? 8 : 20,
            slidesPerView: this.itemSize == 'fixed' ? this.columnsDesktop : 'auto',
          },
        },
      };

      if (typeof this.sliderInstance !== 'object') {
        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);

        if (this.closest('product-type-filter-bar')) {
          sliderOptions.autoHeight = true;
          sliderOptions.allowTouchMove = false;
          sliderOptions.simulateTouch = false;
          sliderOptions.noSwiping = true;
          sliderOptions.noSwipingClass = 'swiper-no-swiping';
          sliderOptions.noSwipingSelector = '.product-type-filter__button, .swiper-no-swiping';
          sliderOptions.preventClicks = false;
          sliderOptions.preventClicksPropagation = false;
          sliderOptions.touchStartPreventDefault = false;
          sliderOptions.threshold = 10;
          sliderOptions.mousewheel = false;
        }

        this.sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions);
        this.sliderInstance.init();

        if (this.closest('product-type-filter-bar')) {
          const updateSwiperHeight = () => {
            if (this.sliderInstance?.slider) {
              this.sliderInstance.slider.update();
            }
          };
          requestAnimationFrame(updateSwiperHeight);
          this.querySelectorAll('img').forEach((img) => {
            if (img.complete) return;
            img.addEventListener('load', updateSwiperHeight, { once: true });
          });
        }

        const focusableElements = FoxTheme.a11y.getFocusableElements(this);
        const isProductTypeFilterBar = Boolean(this.closest('product-type-filter-bar'));

        focusableElements.forEach((element) => {
          element.addEventListener('focusin', () => {
            if (isProductTypeFilterBar) return;

            const slide = element.closest('.swiper-slide');
            this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
          });
        });

        if (Shopify.designMode) {
          document.addEventListener('shopify:block:select', (e) => {
            if (e.detail.sectionId != this.sectionId) return;
            let { target } = e;
            const index = Number(target.dataset.index);

            this.sliderInstance.slider.slideTo(index);
          });
        }
      }
    }

    destroySlider() {
      this.classList.remove(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
      this.sliderWrapper.classList.add(this.classes.grid);
      if (typeof this.sliderInstance !== 'object') return;
      this.sliderInstance.slider.destroy();
      this.sliderInstance = false;
    }
  }
  customElements.define('collection-list', CollectionList);
}
